import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import "./css/uploadDebits.css"; // Import your CSS file for styling
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Badge, Button } from "@deere/fuel-react";

import ToastMessageFunction from "../../common/toast-message-function";
import { useSelector } from "react-redux";
import { Info, AutoAwesome } from "@mui/icons-material";
import { getUploadStatus, uploadFile, downloadTemplate, getTemplateVersion } from "./utils/api";
import { columns } from "./utils/constants";

function UploadDebits() {
  const user = useSelector((state) => state.userState.userData);
  const [files, setFiles] = useState([]);
  const [isSelected, setIsSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [templateVersion, setTemplateVersion] = useState('');
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);

  const toastManager = useRef(ToastMessageFunction());
  const showToast = useCallback(
    (message, severity) => {
      toastManager.current.getToast(message, severity);
    },
    [] // stable ref — never changes between renders
  );

  // Fetch template version on component mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const versionInfo = await getTemplateVersion();
        setTemplateVersion(versionInfo.version);
        
        // Check if there's a new version available
        const lastVersion = localStorage.getItem('lastTemplateVersion');
        if (!lastVersion || lastVersion !== versionInfo.version) {
          // New version available or first time user
          setIsNewVersionAvailable(true);
          if (lastVersion) {
            // Only show toast if there was a previous version (not first time)
            showToast(`New template version (v${versionInfo.version}) available! Please download the latest template.`, 'info');
          }
        } else {
          setIsNewVersionAvailable(false);
        }
      } catch (error) {
        console.error('Failed to fetch template version:', error);
      }
    };
    fetchVersion();
  }, [showToast]);

  
const handleDownload = async () => {
    try {
      await downloadTemplate();
      // Use the current templateVersion from state
      if (templateVersion) {
        localStorage.setItem('lastTemplateVersion', templateVersion);
        setIsNewVersionAvailable(false); // Remove highlight after download
        showToast(`Template downloaded successfully (v${templateVersion})`, 'success');
      } else {
        setIsNewVersionAvailable(false); // Remove highlight even if version not available
        showToast('Template downloaded successfully', 'success');
      }
    } catch (error) {
      console.error('Download failed:', error);
      showToast('Failed to download template', 'error');
    }
  };


  const onDrop = useCallback((acceptedFiles) => {
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );
    setIsSelected(true);
  }, []);

  const onDropRejected = useCallback(
    (rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        if(file.errors[0].message.toLowerCase().includes("too many files")){
           showToast(
          `Only one file can be uploaded at a time.`,
          "error"
        );
        return ;
        }
        showToast(
          `File ${file.file.name} has not an accepted file type. Only .xlsx files are allowed.`,
          "error"
        );
      });
    },
    [showToast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    }, // Allow only .xlsx files
    maxFiles: 1, // Limit to one file
  });

  const handleUpload = async () => {
    if (isSelected && files.length > 0) {
      setLoading(true);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("File", file);
      });
      formData.append("userId", user.userID);

      try {
        const result = await uploadFile(formData, user.userID);

        if (result.success) {
          showToast("Upload successful", "success");

          // Extract the batchUploadId from the message
          const batchUploadId =
            result.transactionId.match(/BatchUploadId: (\d+)/)[1];

          // Add new entry with initial progress
          const newEntry = {
            batchRequestId: batchUploadId,
            progress: 0,
            size: files[0].size, // Add file size here
            name: files[0].name, // Add file name here
            errorMessage: null, // Initialize error message to null
          };
          setData((prevData) => [newEntry, ...prevData]);

          // Start polling for progress updates
          pollProgress(batchUploadId);

          // Reset Dropzone component
          setFiles([]);
          setIsSelected(false);
        }
      } catch (error) {
        showToast("An error occurred during upload.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const pollProgress = (batchRequestId) => {
    const interval = setInterval(async () => {
      try {
        const result = await getUploadStatus(batchRequestId);

        if (result) {
          const { progress, errorMessage } = result.data; // Assuming API returns { progress, errorMessage }
          setData((prevData) =>
            prevData.map((item) =>
              item.batchRequestId === batchRequestId
                ? {
                    ...item,
                    progress,
                    errorMessage,
                    lastUpdate:
                      new Date().toDateString() +
                      " " +
                      new Date().toLocaleTimeString(),
                  }
                : item
            )
          );

          if (progress === 100 || progress === -1) {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error("Error polling progress:", error);
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds
  };

  const hasErrors = data.some(item => {
    // Check if the error message contains "Failed - Invalid file format"
    if (item.errorMessage && item.errorMessage.includes("Invalid file format")) {
      return true; // Indicate that an error has been found
    }
    return false; // Indicate that no relevant error was found for this item
  });
  

  const table = useMaterialReactTable({
    muiCircularProgressProps: {
      color: "secondary",
      thickness: 5,
      size: 55,
    },
    enableExpandAll: false,
    // editableFields: props.editableFields,
    columns: columns,
    data: data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    // enableRowActions: props.enableRowActions,
    enableTopToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnOrdering: true,
    paginationDisplayMode: "pages",
    positionActionsColumn: "last",
    renderTopToolbarCustomActions: () => (
      <>
        <Badge startIcon={<Info />} variant="info">
          Upload progress will not be visible if you navigate away from this
          page.
        </Badge>
      </>
    ),
    mrtTheme: (theme) => ({
      baseBackgroundColor: "rgb(255, 255, 255)",
    }),
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        borderBottom: "1px solid rgb(224, 224, 224)",
      },
    }),
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        borderBottom: "1px solid rgb(224, 224, 224)",
      },
    }),
  });

  return (
    <div className="debit-uploader">
      {hasErrors && (
        <Badge sx={{ marginBottom: "10px" }} startIcon={<Info />} variant="warning">
          The old upload template is no longer supported. Please use the new template to ensure proper functionality.
        </Badge>
      )}
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()}  />
        {isDragActive ? (
          <p>Drop the files here...</p>
        ) : (
          <p>
            Choose file(s) or drag here to upload (Only .xlsx files are allowed,
            one at a time)
          </p>
        )}
      </div>
      <div className="files-list">
        {files.map((file) => (
          <div key={file.path} className="file-item">
            {file.path}
          </div>
        ))}
      </div>
      {toastManager.toast}
      <div className="button-container" style={{ textAlign: "right" }}>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={loading}
          style={{ marginRight: "10px" }}
        >
          {loading ? "Uploading..." : "Upload Transactions"}
        </Button>
        <Button 
          variant="secondary" 
          onClick={handleDownload}
          className={isNewVersionAvailable ? "template-button-highlight" : ""}
          startIcon={isNewVersionAvailable ? <AutoAwesome /> : null}
        >
          Download Template {templateVersion && `(v${templateVersion})`}
        </Button>

      </div>
      <div className="debit-upload-table" style={{ textAlign: "center" }}>
        <MaterialReactTable table={table} columns={columns} data={data} />
      </div>
    </div>
  );
}

export default UploadDebits;
